import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { useRef } from 'react';
import { useRangeCalendarState } from '@react-stately/calendar';
import { useRangeCalendar } from '@react-aria/calendar';
import { useLocale } from '@react-aria/i18n';
import { createCalendar } from '@internationalized/date';
import { CalendarDate } from '@internationalized/date';
import { today, getLocalTimeZone } from '@internationalized/date';
import { CalendarHeader } from './CalendarHeader';
import { CalendarGrid } from './CalendarGrid';
import { DateInput } from './DateInput';
import { DATE_FORMAT } from '@utils/dates/dateMethods';
import { formatDate } from '@utils/dates/date.utils';

const RangeCalendarContainer = styled.div`
    display: flex;
    justify-content: space-evenly;
    column-gap: 12px;
`;

const DateFieldContainer = styled.div`
    display: flex;
    justify-content: space-between;
    margin-bottom: 12px;
`;

export function RangeCalendar({ setCalendarRange }) {
    let { locale } = useLocale();
    const [range, setRange] = useState<{
        startDate: string | null;
        endDate: string | null;
    }>({ startDate: null, endDate: null });
    const { startDate, endDate } = range;
    const [customRange, setCustomRange] = useState({
        startDate: '',
        endDate: '',
    });
    let state = useRangeCalendarState({
        defaultValue: {
            start: today(getLocalTimeZone()),
            end: today(getLocalTimeZone()).subtract({ months: 1 }),
        },
        defaultFocusedValue: today(getLocalTimeZone()).subtract({ months: 1 }),
        visibleDuration: { months: 2 },
        locale,
        createCalendar,
    });

    useEffect(() => {
        if (!state.isDragging && customRange.startDate && customRange.endDate) {
            setCalendarRange(customRange);
        }
    }, [customRange]);

    useEffect(() => {
        if (startDate && startDate !== customRange.startDate) {
            setCustomRange({
                ...customRange,
                startDate: startDate,
            });
        }

        if (endDate && endDate !== customRange.endDate) {
            setCustomRange({
                ...customRange,
                endDate: endDate,
            });
        }
    }, [range]);

    useEffect(() => {
        if (state.highlightedRange) {
            const startDate = formatDate(
                state.highlightedRange.start.toDate(state.timeZone),
                DATE_FORMAT
            );
            const endDate = formatDate(
                state.highlightedRange.end.toDate(state.timeZone),
                DATE_FORMAT
            );
            if (startDate !== range.startDate) {
                setRange({
                    ...range,
                    startDate,
                });
            }

            if (endDate !== range.endDate) {
                setRange({
                    ...range,
                    endDate,
                });
            }
        }
    }, [state]);

    const ref = useRef<HTMLDivElement | null>(null);

    let { calendarProps, prevButtonProps, nextButtonProps } = useRangeCalendar(
        {},
        state,
        ref,
    );

    return (
        <div
            {...calendarProps}
            ref={ref}
            className="inline-block text-gray-800"
        >
            <DateFieldContainer>
                <DateInput
                    id="startDate"
                    value={customRange.startDate}
                    onChange={(val) => {
                        setCustomRange({ ...customRange, startDate: val });
                        state.setValue({
                            start: new CalendarDate(
                                val.getFullYear(),
                                val.getMonth() + 1,
                                val.getDate(),
                            ),
                            end: state.value!.end,
                        });
                    }
                    }
                />

                <DateInput
                    id="endDate"
                    value={customRange.endDate}
                    onChange={(val) => {
                        setCustomRange({ ...customRange, endDate: val });
                        state.setValue({
                            end: new CalendarDate(
                                val.getFullYear(),
                                val.getMonth() + 1,
                                val.getDate(),
                            ),
                            start: state.value!.start,
                        });
                    }}
                />
            </DateFieldContainer>
            <CalendarHeader
                state={state}
                calendarProps={calendarProps}
                prevButtonProps={prevButtonProps}
                nextButtonProps={nextButtonProps}
            />
            <RangeCalendarContainer>
                <CalendarGrid state={state} />
                <CalendarGrid state={state} offset={{ months: 1 }} />
            </RangeCalendarContainer>
        </div>
    );
}
